import { createAction, props } from '@ngrx/store';
import { type } from '@app/util/util';

import {
  TravelerPax, TravelerPaxResponse, TravelerPaxIdentificator, TravelerPaxResponseVm, Loyalty, LoyaltyResponse
} from '@pax/entities/traveler-pax';
import { TravelerInfoType } from '@pax/entities/traveler-info';
import { User } from '@app/modules/core/interfaces/user';
import { TravelerIdentifier, TravelerItemUI } from '@pax/entities/pax-ui';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { UserGender } from '@booking/entities/user-gender';
import { DefaultPassengerConfigUI, DefaultGuestConfigUI, GeneralConfigUI, SetTravelerInterface } from '@pax/entities/default-pax';
import { LoyaltyProgramResponse, MarriottEnrollment, MarriottEnrollmentResponse } from '@pax/entities/loyalty-program.response';
import { MarriottCreateFormUIResponse } from '@pax/entities/marriott-create-form';

export const fetchPaxTravelerListAction = createAction(
  type('[PAX/API] Fetch PAX traveler list'),
  props<{ travelerInfoType: TravelerInfoType, user: User }>()
);
export const fetchPaxTravelerListSuccessAction = createAction(
  type('[PAX/API] Fetch PAX traveler list success'),
  props<{ response: TravelerPaxResponse[] }>()
);
export const createPaxAction = createAction(
  type('[PAX/API] Create PAX'),
  props<{ payload: TravelerPax, identifier: TravelerIdentifier, celebrateAfterCreatedPax: boolean }>()
);
export const createPaxSuccessAction = createAction(
  type('[PAX/API] Create PAX success'),
  props<{ response: TravelerPaxResponse }>()
);
export const updatePaxAction = createAction(
  type('[PAX/API] Update PAX'),
  props<{ payload: TravelerPaxResponseVm, identifier: TravelerIdentifier }>()
);
export const updatePaxSuccessAction = createAction(
  type('[PAX/API] Update PAX success'),
  props<{ response: TravelerPaxResponse }>()
);
export const deletePaxAction = createAction(
  type('[PAX/API] Delete PAX'),
  props<{ payload: TravelerPaxIdentificator, id: number, guestId?: number }>()
);
export const deletePaxSuccessAction = createAction(
  type('[PAX/API] Delete PAX success'),
  props<{ payload: TravelerPaxIdentificator, id: number, guestId?: number }>()
);
export const deletePaxDocumentSuccessAction = createAction(
  type('[PAX/API] Delete PAX Document success'),
  props<{ payload: TravelerPaxIdentificator, id: number, guestId?: number }>()
);

export const onlyAddToListAction = createAction(
  type('[PAX/UI] Only add PAX to list'),
  props<{ payload: TravelerPax, identifier: TravelerIdentifier }>()
);
export const onlyUpdateToListAction = createAction(
  type('[PAX/UI] Only update PAX to List'),
  props<{ payload: TravelerPaxResponseVm, identifier: TravelerIdentifier }>()
);
export const onlyDeleteToListAction = createAction(
  type('[PAX/UI] Only delete PAX to List'),
  props<{ payload: TravelerPaxIdentificator, id: number, guestId?: number }>()
);

export const setCreatePAXUIAction = createAction(
  type('[PAX/UI] Set create new pax UI action'),
  props<{ payload: boolean }>()
);
export const configDefaultPassengerUI = createAction(
  type('[PAX/UI] Config default passenger UI'),
  props<{ config: DefaultPassengerConfigUI  }>()
);
export const configDefaultGuestUI = createAction(
  type('[PAX/UI] Config default guest UI'),
  props<{ config: DefaultGuestConfigUI }>()
);
export const configDefaultByInfoTypeAction = createAction(
  type('[PAX/UI] Config default PAX by type'),
  props<{ config: GeneralConfigUI }>()
);
export const setPassengerDefaultDataUIAction = createAction(
  type('[PAX/UI] Set passenger default data UI'),
  props<{
    payload: TravelerPaxResponse,
    id: number,
    list: TravelerPaxResponse[],
    defaultAirline: string
  }>()
);
export const setGuestDefaultDataUIAction = createAction(
  type('[PAX/UI] Set guest default data UI'),
  props<{
    payload: TravelerPaxResponse,
    roomId: number,
    guestId: number,
    list: TravelerPaxResponse[]
  }>()
);
export const setTavelerPaxAction = createAction(
  type('[PAX/UI] Set PAX traveler'),
  props<{ config: SetTravelerInterface }>()
);
export const setBookingTypeAction = createAction(
  type('[PAX/UI] Set booking type UI action'),
  props<{ payload: TravelBookingType }>()
);
export const removeDefaultPaxAction = createAction(
  type('[PAX/UI] Remove default Pax'),
  props<{ id: number }>()
);
export const removeDefaultPaxByGuestRoomAction = createAction(
  type('[PAX/UI] Remove default Pax by guest room'),
  props<{ guestId: number, roomIndex: number }>()
);
export const setIsActiveCelebratePaxViewAction = createAction(
  type('[PAX/UI] Set is celebrate pax view action'),
  props<{ payload: UserGender }>()
);
export const setLoadingTravelerForm = createAction(
  type('[PAX/UI] Set Loading button Traveler Form'),
  props<{ isLoadingTravelerForm: boolean }>()
);

export const addLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/UI] Add loyalty program'),
  props<{ payload: Loyalty, id: number }>()
);
export const setLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/UI] Set loyalty program selection'),
  props<{ payload: LoyaltyResponse, id: number }>()
);
export const createLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Create loyalty program'),
  props<{ payload: Loyalty, id: number }>()
);
export const createLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Create loyalty program success'),
  props<{ response: LoyaltyResponse, id: number }>()
);
export const updateLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Update loyalty program'),
  props<{ payload: LoyaltyResponse, id: number }>()
);
export const updateLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Update loyalty program success'),
  props<{ response: LoyaltyResponse, id: number }>()
);
export const deleteLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Delete loyalty program'),
  props<{ payload: LoyaltyResponse, id: number }>()
);
export const deleteLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Delete loyalty program success'),
  props<{ response: LoyaltyResponse, id: number }>()
);

export const addPackageLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/UI] Add Package loyalty program'),
  props<{ payload: Loyalty, id: number, guestId: number }>()
);
export const setPackageLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/UI] Set Package loyalty program selection'),
  props<{ payload: LoyaltyResponse, id: number, guestId: number }>()
);
export const createPackageLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Create Package loyalty program'),
  props<{ payload: Loyalty, id: number, guestId: number }>()
);
export const createPackageLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Create Package loyalty program success'),
  props<{ response: LoyaltyResponse, id: number, guestId: number }>()
);
export const updatePackageLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Update Package loyalty program'),
  props<{ payload: LoyaltyResponse, id: number, guestId: number }>()
);
export const updatePackageLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Update Package loyalty program success'),
  props<{ response: LoyaltyResponse, id: number, guestId: number }>()
);
export const deletePackageLoyaltyProgramAction = createAction(
  type('[PAX/LOYALTY/API] Delete Package loyalty program'),
  props<{ payload: LoyaltyResponse, id: number, guestId: number }>()
);
export const deletePackageLoyaltyProgramSuccessAction = createAction(
  type('[PAX/LOYALTY/API] Delete Package loyalty program success'),
  props<{ response: LoyaltyResponse, id: number, guestId: number }>()
);
export const fetchMarriottLoyaltyNumber = createAction(
  type('[PAX/LOYALTY/API] Fetch Marriott loyalty number'),
  props<{ payload: number }>()
);
export const fetchMarriottLoyaltyNumberSuccess = createAction(
  type('[PAX/LOYALTY/API] Fetch Marriott loyalty number success'),
  props<{ response: LoyaltyProgramResponse }>()
);
export const createMarriottBonvoyAccount = createAction(
  type('[PAX/LOYALTY/API] Create Marriott Bonvoy account'),
  props<{ payload: MarriottEnrollment }>()
);
export const createMarriottBonvoyAccountSuccess = createAction(
  type('[PAX/LOYALTY/API] Create Marriott Bonvoy account success'),
  props<{ response: MarriottEnrollmentResponse }>()
);
export const createMarriottBonvoyAccountShowStatus = createAction(
  type('[PAX/LOYALTY/API] Create Marriott Bonvoy account show status'),
  props<{ response: MarriottCreateFormUIResponse }>()
);
export const resetMarriottCreateAccountShowStatus = createAction(
  type('[PAX/LOYALTY/API] Reset Create Marriott Bonvoy account show status')
);
export const resetActiveBonvoyNumberAction = createAction(
  type('[PAX/LOYALTY/API] Reset Active Marriott Bonvoy number')
);

export const setPassengerListUiAction = createAction(
  type('[PAX/UI] Set passenger list ui.'),
  props<{ list: TravelerItemUI[] }>()
);
